import { Button } from '@heroui/react'
import { getCurrentUser, queryKeys, useSetupUserDrive } from '@qse/api'
import { useLogout } from '@qse/auth-ui'
import type { ModalComponentProps } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useJiraFeedback } from '../../hooks/useJiraFeedback'

interface DriveSetupModalData {
  onSuccess?: () => void
}

export default function DriveSetupModal(props: ModalComponentProps) {
  const { data } = props
  const modalData = data as DriveSetupModalData
  const { user } = useUserStore()
  const setupDriveMutation = useSetupUserDrive()
  const queryClient = useQueryClient()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [retryCountdown, setRetryCountdown] = useState(0)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { openFeedbackDialog } = useJiraFeedback()
  const { handleLogout } = useLogout()

  // Handle retry countdown
  useEffect(() => {
    if (retryCountdown > 0) {
      const timer = setTimeout(() => setRetryCountdown(prev => prev - 1), 1000)

      return () => clearTimeout(timer)
    }
  }, [retryCountdown])

  const handleContinueSetup = async () => {
    if (!user?.id || retryCountdown > 0) return

    setErrorMessage(null)

    try {
      await setupDriveMutation.mutateAsync(user.id)

      // Manually fetch updated user data and update cache
      const updatedUser = await getCurrentUser()

      queryClient.setQueryData(queryKeys.auth.me(), updatedUser)

      // Reset file and quota queries to clear cache
      await Promise.all([
        queryClient.resetQueries({ queryKey: queryKeys.files.all }),
        queryClient.resetQueries({ queryKey: queryKeys.quota.all }),
      ])

      // Show success toast and close modal
      toast.success('Drive setup completed successfully')
      modalData.onSuccess?.()
    } catch (_error: any) {
      console.error('Error setting up drive:', _error)
      toast.error(_error.message || 'Failed to setup drive. Please try again.')
      setErrorMessage(
        "We're experiencing temporary technical difficulties. Please try again in a few minutes.",
      )
      setRetryCountdown(10)
    }
  }

  const handleContactSupport = () => {
    openFeedbackDialog()
  }

  const handleLogoutClick = async () => {
    setIsLoggingOut(true)
    // Keep the modal open during logout by skipping modal store reset
    await handleLogout({ skipModalStore: true })
  }

  const isLoading = setupDriveMutation.isPending
  const buttonText =
    retryCountdown > 0
      ? `Try again (${retryCountdown}s)`
      : errorMessage
        ? 'Try again'
        : 'Continue setup'

  return (
    <div className="flex items-center justify-center py-8">
      <div className="flex flex-col items-center text-center gap-4 max-w-md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Drive Setup Required
        </h2>

        <div className="fixed right-10 top-6 z-10">
          <Button
            className="bg-white text-black"
            endContent={<ArrowLeftIcon className="w-4 h-4 rotate-180" />}
            isLoading={isLoggingOut}
            radius="sm"
            size="lg"
            onPress={handleLogoutClick}
          >
            Logout
          </Button>
        </div>

        <p className="text-gray-600 dark:text-gray-300">
          {isLoggingOut
            ? 'Logging out...'
            : isLoading
              ? 'Setting up your secure storage...'
              : errorMessage ||
                "We're still preparing your secure storage. This should only take a moment."}
        </p>

        <div className="flex gap-3 mt-2">
          <Button
            color="primary"
            isDisabled={retryCountdown > 0 || isLoggingOut}
            isLoading={isLoading}
            onPress={handleContinueSetup}
          >
            {buttonText}
          </Button>
          <Button
            color="default"
            isDisabled={isLoading || isLoggingOut}
            variant="bordered"
            onPress={handleContactSupport}
          >
            Contact support
          </Button>
        </div>
      </div>
    </div>
  )
}
